/**
 * Languages Tab for Show Edit Page
 *
 * Multi-language show translations management.
 * Displays translation cards based on selected language filter.
 *
 * @module ShowEditLanguagesTab
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import type { ShowTranslation, Language } from '@musedrops/shared';
import { logger } from '@musedrops/shared';
import { ShowTranslationCard } from './ShowTranslationCard';
import { getShowTranslations } from '../../../data/showTranslation';
import { LanguageApi } from '../../../services/languageApi';
import { LanguageSelector } from '../../../shared/components/LanguageSelector';
import { supabase } from '../../../services/supabase';

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
type ChangeHandler<T> = (value: T) => void;

interface ShowEditLanguagesTabProps {
  showId: string;
  showSlug: string;
  showLang: string | null; // null = "All Languages"
  onLanguageChange?: ChangeHandler<string | null>; // Callback to update show's lang field
}

/**
 * Languages tab content for ShowEdit page
 */
export const ShowEditLanguagesTab: React.FC<ShowEditLanguagesTabProps> = ({
  showId,
  showSlug,
  showLang,
  onLanguageChange,
}) => {
  // State
  const [languages, setLanguages] = useState<Language[]>([]);
  const [translations, setTranslations] = useState<ShowTranslation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selected language filter (local state, defaults to showLang)
  const [selectedLang, setSelectedLang] = useState<string | null>(showLang);

  // Warning dialog state
  const [showWarning, setShowWarning] = useState(false);
  const [pendingLang, setPendingLang] = useState<string | null>(null);

  // Filter languages based on selected language
  const displayLanguages = React.useMemo(() => {
    if (selectedLang) {
      // Specific language selected - show only that language
      return languages.filter(l => l.key === selectedLang);
    }
    // "All Languages" selected - show all languages that have include_in_global_gen
    return languages.filter(l => l.includeInGlobalGen);
  }, [languages, selectedLang]);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [allLanguages, showTranslations] = await Promise.all([
        LanguageApi.getAll(),
        getShowTranslations(showSlug),
      ]);

      setLanguages(allLanguages);
      setTranslations(showTranslations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      logger.error('Failed to load languages tab data', { error: err });
    } finally {
      setLoading(false);
    }
  }, [showSlug]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Subscribe to translation updates
  useEffect(() => {
    const subscription = supabase
      .channel(`show-translations-${showSlug}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'show_translations',
          filter: `show_slug=eq.${showSlug}`,
        },
        () => {
          // Reload translations when any change occurs
          loadData();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [showSlug, loadData]);

  // Handle translation update from card
  const handleTranslationUpdated = useCallback((updated: ShowTranslation) => {
    setTranslations(prev =>
      prev.map(t =>
        t.showSlug === updated.showSlug && t.lang === updated.lang ? updated : t
      )
    );
  }, []);

  // Calculate languages that would be displayed for a given selection
  const getLanguagesForSelection = useCallback(
    (lang: string | null): Language[] => {
      if (lang) {
        return languages.filter(l => l.key === lang);
      }
      return languages.filter(l => l.includeInGlobalGen);
    },
    [languages]
  );

  // Calculate which languages will be removed when changing selection
  const languagesToRemove = React.useMemo(() => {
    if (pendingLang === null && selectedLang === null) return [];

    const newLangs = new Set(getLanguagesForSelection(pendingLang).map(l => l.key));

    // Find languages that are in current but not in new
    return displayLanguages.filter(l => !newLangs.has(l.key));
  }, [displayLanguages, pendingLang, selectedLang, getLanguagesForSelection]);

  // Handle language selection change with warning
  const handleLanguageChange = (newLang: string | null) => {
    // Calculate if any languages will be removed
    const newLangs = new Set(getLanguagesForSelection(newLang).map(l => l.key));
    const willRemove = displayLanguages.some(l => !newLangs.has(l.key));

    if (willRemove) {
      // Show warning dialog before applying change
      setPendingLang(newLang);
      setShowWarning(true);
    } else {
      // No languages being removed, apply immediately
      setSelectedLang(newLang);
      // Update parent's lang field so it gets saved
      onLanguageChange?.(newLang);
    }
  };

  // Confirm language change
  const handleConfirmChange = () => {
    setSelectedLang(pendingLang);
    // Update parent's lang field so it gets saved
    onLanguageChange?.(pendingLang);
    setShowWarning(false);
    setPendingLang(null);
  };

  // Cancel language change
  const handleCancelChange = () => {
    setShowWarning(false);
    setPendingLang(null);
  };

  // Get translation for a language
  const getTranslation = (langKey: string): ShowTranslation | null => {
    return translations.find(t => t.lang === langKey) || null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Header with language selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h6">Translations</Typography>
        <LanguageSelector
          value={selectedLang}
          onChange={handleLanguageChange}
          label="Language"
          showAllOption
          allOptionLabel="All global languages"
          width="medium"
        />
      </Box>

      {/* Translation Cards */}
      {displayLanguages.length === 0 ? (
        <Alert severity="info">
          No languages available for this selection.
        </Alert>
      ) : (
        <Box>
          {displayLanguages.map(lang => (
            <ShowTranslationCard
              key={lang.key}
              showId={showId}
              showSlug={showSlug}
              language={lang}
              translation={getTranslation(lang.key)}
              onTranslationUpdated={handleTranslationUpdated}
            />
          ))}
        </Box>
      )}

      {/* Warning Dialog for language change */}
      <Dialog open={showWarning} onClose={handleCancelChange}>
        <DialogTitle color="error">Delete Translations?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This change will DELETE translations for shows AND stories in:
            <Box component="ul" sx={{ mt: 1, mb: 2, pl: 2 }}>
              {languagesToRemove.map(lang => (
                <li key={lang.key}>
                  <strong>{lang.name}</strong>
                </li>
              ))}
            </Box>
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelChange}>Cancel</Button>
          <Button
            onClick={handleConfirmChange}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
